import { useState, useEffect, useMemo } from "react";
import axios from "axios";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Boxes, Plus, Pencil, Trash2, Search, AlertTriangle, PackagePlus, PackageMinus, History } from "lucide-react";
import { toast } from "sonner";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const CATEGORIES = [
  { value: "prob", label: "Prob" },
  { value: "sternafix", label: "Sternafix" },
  { value: "cihaz", label: "Cihaz" },
  { value: "diger", label: "Diğer" },
];

const ProductInventory = () => {
  const [products, setProducts] = useState([]);
  const [searchTerm, setSearchTerm] = useState("");
  const [filterCategory, setFilterCategory] = useState("all");
  const [showLowStock, setShowLowStock] = useState(false);
  const [isAddDialogOpen, setIsAddDialogOpen] = useState(false);
  const [isEditDialogOpen, setIsEditDialogOpen] = useState(false);
  const [isStockDialogOpen, setIsStockDialogOpen] = useState(false);
  const [isLogDialogOpen, setIsLogDialogOpen] = useState(false);
  const [editingProduct, setEditingProduct] = useState(null);
  const [stockProduct, setStockProduct] = useState(null);
  const [stockLogs, setStockLogs] = useState([]);
  const [stockAdjust, setStockAdjust] = useState({ type: "add", quantity: 0, reason: "" });
  const [formData, setFormData] = useState({
    name: "",
    sku: "",
    category: "prob",
    unit_price: 0,
    stock_quantity: 0,
    min_stock_level: 10,
    sut_no: "",
    ubb_no: "",
    notes: "",
  });

  const fetchProducts = async () => {
    try {
      const params = new URLSearchParams();
      if (filterCategory !== "all") params.append("category", filterCategory);
      const res = await axios.get(`${API}/products?${params.toString()}`);
      setProducts(res.data);
    } catch (error) {
      console.error("Error fetching products:", error);
      toast.error("Ürünler yüklenirken hata oluştu");
    }
  };

  useEffect(() => {
    fetchProducts();
  }, [filterCategory]);

  const filteredProducts = useMemo(() => {
    let result = products;
    if (searchTerm) {
      const term = searchTerm.toLowerCase();
      result = result.filter(p =>
        p.name.toLowerCase().includes(term) ||
        p.sku.toLowerCase().includes(term) ||
        p.sut_no.toLowerCase().includes(term) ||
        p.ubb_no.toLowerCase().includes(term)
      );
    }
    if (showLowStock) {
      result = result.filter(p => p.stock_quantity <= p.min_stock_level);
    }
    return result;
  }, [products, searchTerm, showLowStock]);

  const lowStockCount = products.filter(p => p.stock_quantity <= p.min_stock_level).length;
  const totalStockValue = products.reduce((sum, p) => sum + (p.stock_quantity * p.unit_price), 0);

  const resetForm = () => {
    setFormData({ name: "", sku: "", category: "prob", unit_price: 0, stock_quantity: 0, min_stock_level: 10, sut_no: "", ubb_no: "", notes: "" });
  };

  const handleAdd = async (e) => {
    e.preventDefault();
    try {
      await axios.post(`${API}/products`, formData);
      toast.success("Ürün eklendi");
      setIsAddDialogOpen(false);
      resetForm();
      fetchProducts();
    } catch (error) {
      toast.error("Ürün eklenirken hata oluştu");
    }
  };

  const handleEdit = (product) => {
    setEditingProduct(product);
    setFormData({
      name: product.name,
      sku: product.sku || "",
      category: product.category,
      unit_price: product.unit_price,
      stock_quantity: product.stock_quantity,
      min_stock_level: product.min_stock_level,
      sut_no: product.sut_no || "",
      ubb_no: product.ubb_no || "",
      notes: product.notes || "",
    });
    setIsEditDialogOpen(true);
  };

  const handleUpdate = async (e) => {
    e.preventDefault();
    if (!editingProduct) return;
    try {
      await axios.put(`${API}/products/${editingProduct.id}`, formData);
      toast.success("Ürün güncellendi");
      setIsEditDialogOpen(false);
      setEditingProduct(null);
      resetForm();
      fetchProducts();
    } catch (error) {
      toast.error("Ürün güncellenirken hata oluştu");
    }
  };

  const handleDelete = async (product) => {
    if (!window.confirm(`"${product.name}" ürününü silmek istediğinizden emin misiniz?`)) return;
    try {
      await axios.delete(`${API}/products/${product.id}`);
      toast.success("Ürün silindi");
      fetchProducts();
    } catch (error) {
      toast.error("Ürün silinirken hata oluştu");
    }
  };

  const openStockDialog = (product, type) => {
    setStockProduct(product);
    setStockAdjust({ type, quantity: 0, reason: "" });
    setIsStockDialogOpen(true);
  };

  const handleStockAdjust = async () => {
    if (!stockProduct || stockAdjust.quantity <= 0) return;
    const qty = stockAdjust.type === "add" ? stockAdjust.quantity : -stockAdjust.quantity;
    try {
      await axios.post(`${API}/products/${stockProduct.id}/stock-adjust`, {
        quantity: qty,
        reason: stockAdjust.reason || (stockAdjust.type === "add" ? "Stok girişi" : "Stok çıkışı"),
      });
      toast.success(stockAdjust.type === "add" ? "Stok eklendi" : "Stok düşüldü");
      setIsStockDialogOpen(false);
      setStockProduct(null);
      fetchProducts();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Stok güncellenirken hata oluştu");
    }
  };

  const openLogDialog = async (product) => {
    setStockProduct(product);
    try {
      const res = await axios.get(`${API}/products/stock-logs/${product.id}`);
      setStockLogs(res.data);
    } catch {
      setStockLogs([]);
    }
    setIsLogDialogOpen(true);
  };

  const ProductForm = ({ onSubmit, submitLabel }) => (
    <form onSubmit={onSubmit} className="space-y-4">
      <div className="grid grid-cols-2 gap-4">
        <div className="col-span-2">
          <Label>Ürün Adı *</Label>
          <Input data-testid="product-name-input" placeholder="Örn: Shouliang Prob 3.0" value={formData.name} onChange={(e) => setFormData({ ...formData, name: e.target.value })} required />
        </div>
        <div>
          <Label>SKU / Ürün Kodu</Label>
          <Input data-testid="product-sku-input" placeholder="Örn: SHL-P30" value={formData.sku} onChange={(e) => setFormData({ ...formData, sku: e.target.value })} />
        </div>
        <div>
          <Label>Kategori *</Label>
          <Select value={formData.category} onValueChange={(v) => setFormData({ ...formData, category: v })}>
            <SelectTrigger data-testid="product-category-select"><SelectValue /></SelectTrigger>
            <SelectContent>
              {CATEGORIES.map(c => <SelectItem key={c.value} value={c.value}>{c.label}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>
        <div>
          <Label>SUT No</Label>
          <Input data-testid="product-sut-input" placeholder="SUT numarası" value={formData.sut_no} onChange={(e) => setFormData({ ...formData, sut_no: e.target.value })} />
        </div>
        <div>
          <Label>UBB No</Label>
          <Input data-testid="product-ubb-input" placeholder="UBB barkod numarası" value={formData.ubb_no} onChange={(e) => setFormData({ ...formData, ubb_no: e.target.value })} />
        </div>
        <div>
          <Label>Birim Fiyat (TL)</Label>
          <Input type="number" min="0" step="0.01" data-testid="product-price-input" value={formData.unit_price} onChange={(e) => setFormData({ ...formData, unit_price: parseFloat(e.target.value) || 0 })} />
        </div>
        <div>
          <Label>Başlangıç Stok</Label>
          <Input type="number" min="0" data-testid="product-stock-input" value={formData.stock_quantity} onChange={(e) => setFormData({ ...formData, stock_quantity: parseInt(e.target.value) || 0 })} />
        </div>
        <div>
          <Label>Min. Stok Uyarı Seviyesi</Label>
          <Input type="number" min="0" data-testid="product-min-stock-input" placeholder="10" value={formData.min_stock_level} onChange={(e) => setFormData({ ...formData, min_stock_level: parseInt(e.target.value) || 0 })} />
        </div>
        <div className="col-span-2">
          <Label>Notlar</Label>
          <Input data-testid="product-notes-input" placeholder="Ek bilgiler..." value={formData.notes} onChange={(e) => setFormData({ ...formData, notes: e.target.value })} />
        </div>
      </div>
      <div className="flex gap-3 pt-4">
        <Button type="button" variant="outline" className="flex-1" onClick={() => { setIsAddDialogOpen(false); setIsEditDialogOpen(false); setEditingProduct(null); resetForm(); }} data-testid="cancel-product-btn">İptal</Button>
        <Button type="submit" className="flex-1" data-testid="save-product-btn">{submitLabel}</Button>
      </div>
    </form>
  );

  return (
    <div className="space-y-6" data-testid="product-inventory-page">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-4xl md:text-5xl font-bold tracking-tight" data-testid="product-inventory-title">
            Ürün Envanteri
          </h1>
          <p className="text-base text-muted-foreground mt-2">
            Medikal ürün stok takibi ve yönetimi
          </p>
        </div>
        <Button onClick={() => { resetForm(); setIsAddDialogOpen(true); }} data-testid="add-product-btn">
          <Plus className="w-4 h-4 mr-2" />
          Yeni Ürün Ekle
        </Button>
      </div>

      {/* Summary Cards */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4" data-testid="product-summary">
        <Card className="bg-blue-50 border-blue-200">
          <CardContent className="p-4">
            <p className="text-sm text-blue-600 font-medium">Toplam Ürün</p>
            <p className="text-2xl font-bold text-blue-700 mt-1">{products.length}</p>
          </CardContent>
        </Card>
        <Card className="bg-purple-50 border-purple-200">
          <CardContent className="p-4">
            <p className="text-sm text-purple-600 font-medium">Toplam Stok Değeri</p>
            <p className="text-2xl font-bold text-purple-700 mt-1">
              {totalStockValue.toLocaleString('tr-TR', { minimumFractionDigits: 2 })} TL
            </p>
          </CardContent>
        </Card>
        <Card className={`${lowStockCount > 0 ? 'bg-red-50 border-red-300' : 'bg-green-50 border-green-200'}`}>
          <CardContent className="p-4">
            <p className={`text-sm font-medium ${lowStockCount > 0 ? 'text-red-600' : 'text-green-600'}`}>Düşük Stok Uyarısı</p>
            <p className={`text-2xl font-bold mt-1 ${lowStockCount > 0 ? 'text-red-700' : 'text-green-700'}`}>
              {lowStockCount > 0 ? `${lowStockCount} ürün` : "Yok"}
            </p>
          </CardContent>
        </Card>
        <Card className="bg-emerald-50 border-emerald-200">
          <CardContent className="p-4">
            <p className="text-sm text-emerald-600 font-medium">Aktif Kategori</p>
            <p className="text-2xl font-bold text-emerald-700 mt-1">
              {new Set(products.map(p => p.category)).size}
            </p>
          </CardContent>
        </Card>
      </div>

      {/* Filters */}
      <Card>
        <CardContent className="p-4">
          <div className="flex gap-3 flex-wrap items-center">
            <div className="relative flex-1 min-w-[200px] max-w-sm">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
              <Input
                placeholder="Ürün adı, SKU, SUT veya UBB ile ara..."
                data-testid="product-search"
                className="pl-10"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
              />
            </div>
            <Select value={filterCategory} onValueChange={setFilterCategory}>
              <SelectTrigger className="w-40" data-testid="product-category-filter">
                <SelectValue placeholder="Kategori" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Tümü</SelectItem>
                {CATEGORIES.map(c => <SelectItem key={c.value} value={c.value}>{c.label}</SelectItem>)}
              </SelectContent>
            </Select>
            <Button
              variant={showLowStock ? "destructive" : "outline"}
              size="sm"
              onClick={() => setShowLowStock(!showLowStock)}
              data-testid="low-stock-filter-btn"
            >
              <AlertTriangle className="w-4 h-4 mr-2" />
              {showLowStock ? "Tümünü Göster" : `Düşük Stok (${lowStockCount})`}
            </Button>
          </div>
        </CardContent>
      </Card>

      {/* Products Table */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Boxes className="w-5 h-5" />
            Ürünler ({filteredProducts.length})
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="overflow-x-auto">
            <table className="w-full" data-testid="product-table">
              <thead>
                <tr className="border-b border-border">
                  <th className="text-left py-3 px-4 font-semibold text-sm">Ürün Adı</th>
                  <th className="text-left py-3 px-4 font-semibold text-sm">SKU</th>
                  <th className="text-left py-3 px-4 font-semibold text-sm">Kategori</th>
                  <th className="text-left py-3 px-4 font-semibold text-sm">SUT No</th>
                  <th className="text-left py-3 px-4 font-semibold text-sm">UBB No</th>
                  <th className="text-right py-3 px-4 font-semibold text-sm">Birim Fiyat</th>
                  <th className="text-center py-3 px-4 font-semibold text-sm">Stok</th>
                  <th className="text-center py-3 px-4 font-semibold text-sm">Stok İşlemleri</th>
                  <th className="text-center py-3 px-4 font-semibold text-sm">İşlemler</th>
                </tr>
              </thead>
              <tbody>
                {filteredProducts.map((product) => {
                  const isLowStock = product.stock_quantity <= product.min_stock_level;
                  return (
                    <tr
                      key={product.id}
                      className={`border-b border-border table-row ${isLowStock ? 'bg-red-50' : ''}`}
                      data-testid={`product-row-${product.id}`}
                    >
                      <td className="py-3 px-4">
                        <div className="flex items-center gap-2">
                          {isLowStock && <AlertTriangle className="w-4 h-4 text-red-500 flex-shrink-0" />}
                          <span className="font-medium">{product.name}</span>
                        </div>
                      </td>
                      <td className="py-3 px-4 text-muted-foreground font-mono text-sm">{product.sku || "-"}</td>
                      <td className="py-3 px-4">
                        <span className="text-xs px-2 py-1 rounded bg-slate-100 text-slate-700 font-medium">
                          {CATEGORIES.find(c => c.value === product.category)?.label || product.category}
                        </span>
                      </td>
                      <td className="py-3 px-4 text-muted-foreground text-sm">{product.sut_no || "-"}</td>
                      <td className="py-3 px-4 text-muted-foreground text-sm">{product.ubb_no || "-"}</td>
                      <td className="py-3 px-4 text-right font-medium">
                        {product.unit_price.toLocaleString('tr-TR', { minimumFractionDigits: 2 })} TL
                      </td>
                      <td className="py-3 px-4 text-center">
                        <div className="flex flex-col items-center">
                          <span className={`text-xl font-bold ${isLowStock ? 'text-red-600' : 'text-green-600'}`}>
                            {product.stock_quantity}
                          </span>
                          <span className="text-xs text-muted-foreground">min: {product.min_stock_level}</span>
                        </div>
                      </td>
                      <td className="py-3 px-4">
                        <div className="flex items-center justify-center gap-1">
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => openStockDialog(product, "add")}
                            data-testid={`stock-add-${product.id}`}
                            className="h-8 px-2 text-green-600 border-green-300 hover:bg-green-50"
                          >
                            <PackagePlus className="w-4 h-4" />
                          </Button>
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => openStockDialog(product, "remove")}
                            data-testid={`stock-remove-${product.id}`}
                            className="h-8 px-2 text-red-600 border-red-300 hover:bg-red-50"
                          >
                            <PackageMinus className="w-4 h-4" />
                          </Button>
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => openLogDialog(product)}
                            data-testid={`stock-log-${product.id}`}
                            className="h-8 px-2"
                          >
                            <History className="w-4 h-4" />
                          </Button>
                        </div>
                      </td>
                      <td className="py-3 px-4">
                        <div className="flex items-center justify-center gap-1">
                          <Button variant="ghost" size="sm" onClick={() => handleEdit(product)} data-testid={`edit-product-${product.id}`}>
                            <Pencil className="w-4 h-4" />
                          </Button>
                          <Button variant="ghost" size="sm" onClick={() => handleDelete(product)} data-testid={`delete-product-${product.id}`} className="text-destructive">
                            <Trash2 className="w-4 h-4" />
                          </Button>
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
            {filteredProducts.length === 0 && (
              <div className="text-center py-12" data-testid="no-products">
                <Boxes className="w-12 h-12 mx-auto text-muted-foreground mb-4" />
                <p className="text-muted-foreground">
                  {searchTerm || showLowStock ? "Filtreye uygun ürün bulunamadı" : "Henüz ürün eklenmemiş"}
                </p>
              </div>
            )}
          </div>
        </CardContent>
      </Card>

      {/* Add Product Dialog */}
      <Dialog open={isAddDialogOpen} onOpenChange={setIsAddDialogOpen}>
        <DialogContent className="max-w-2xl" data-testid="add-product-dialog">
          <DialogHeader><DialogTitle className="flex items-center gap-2"><Boxes className="w-5 h-5" />Yeni Ürün Ekle</DialogTitle></DialogHeader>
          <ProductForm onSubmit={handleAdd} submitLabel="Ekle" />
        </DialogContent>
      </Dialog>

      {/* Edit Product Dialog */}
      <Dialog open={isEditDialogOpen} onOpenChange={(open) => { setIsEditDialogOpen(open); if (!open) { setEditingProduct(null); resetForm(); } }}>
        <DialogContent className="max-w-2xl" data-testid="edit-product-dialog">
          <DialogHeader><DialogTitle className="flex items-center gap-2"><Pencil className="w-5 h-5" />Ürünü Düzenle</DialogTitle></DialogHeader>
          <ProductForm onSubmit={handleUpdate} submitLabel="Güncelle" />
        </DialogContent>
      </Dialog>

      {/* Stock Adjustment Dialog */}
      <Dialog open={isStockDialogOpen} onOpenChange={setIsStockDialogOpen}>
        <DialogContent className="max-w-md" data-testid="stock-adjust-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              {stockAdjust.type === "add" ? <PackagePlus className="w-5 h-5 text-green-600" /> : <PackageMinus className="w-5 h-5 text-red-600" />}
              {stockAdjust.type === "add" ? "Stok Girişi" : "Stok Çıkışı"}
            </DialogTitle>
          </DialogHeader>
          {stockProduct && (
            <div className="space-y-4">
              <div className="p-4 bg-secondary rounded-lg">
                <p className="font-semibold">{stockProduct.name}</p>
                <p className="text-sm text-muted-foreground">Mevcut Stok: <span className="font-bold text-foreground">{stockProduct.stock_quantity}</span></p>
              </div>
              <div>
                <Label>Miktar *</Label>
                <Input
                  type="number"
                  min="1"
                  data-testid="stock-quantity-input"
                  value={stockAdjust.quantity}
                  onChange={(e) => setStockAdjust({ ...stockAdjust, quantity: parseInt(e.target.value) || 0 })}
                  autoFocus
                />
              </div>
              <div>
                <Label>Açıklama</Label>
                <Input
                  data-testid="stock-reason-input"
                  placeholder={stockAdjust.type === "add" ? "Örn: Tedarikçiden alım" : "Örn: Hasarlı ürün"}
                  value={stockAdjust.reason}
                  onChange={(e) => setStockAdjust({ ...stockAdjust, reason: e.target.value })}
                />
              </div>
              {stockAdjust.quantity > 0 && (
                <div className="p-3 rounded-lg bg-slate-50 border text-sm">
                  <span className="text-muted-foreground">Yeni stok: </span>
                  <span className="font-bold">
                    {stockAdjust.type === "add"
                      ? stockProduct.stock_quantity + stockAdjust.quantity
                      : Math.max(0, stockProduct.stock_quantity - stockAdjust.quantity)}
                  </span>
                </div>
              )}
              <div className="flex gap-3 pt-2">
                <Button variant="outline" className="flex-1" onClick={() => setIsStockDialogOpen(false)} data-testid="cancel-stock-btn">İptal</Button>
                <Button
                  className={`flex-1 ${stockAdjust.type === "add" ? "bg-green-600 hover:bg-green-700" : "bg-red-600 hover:bg-red-700"}`}
                  onClick={handleStockAdjust}
                  disabled={stockAdjust.quantity <= 0}
                  data-testid="confirm-stock-btn"
                >
                  {stockAdjust.type === "add" ? "Stok Ekle" : "Stok Düş"}
                </Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Stock Log Dialog */}
      <Dialog open={isLogDialogOpen} onOpenChange={setIsLogDialogOpen}>
        <DialogContent className="max-w-lg" data-testid="stock-log-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <History className="w-5 h-5" />
              Stok Geçmişi - {stockProduct?.name}
            </DialogTitle>
          </DialogHeader>
          <div className="max-h-[400px] overflow-y-auto">
            {stockLogs.length === 0 ? (
              <p className="text-center text-muted-foreground py-8">Henüz stok hareketi yok</p>
            ) : (
              <div className="space-y-2">
                {stockLogs.map((log) => (
                  <div key={log.id} className="p-3 rounded-lg bg-secondary text-sm flex justify-between items-center">
                    <div>
                      <div className="flex items-center gap-2">
                        <span className={`font-bold ${log.adjustment > 0 ? 'text-green-600' : 'text-red-600'}`}>
                          {log.adjustment > 0 ? `+${log.adjustment}` : log.adjustment}
                        </span>
                        <span className="text-xs text-muted-foreground">{log.reason}</span>
                      </div>
                      <p className="text-xs text-muted-foreground mt-1">
                        {log.previous_stock} &rarr; {log.new_stock}
                      </p>
                    </div>
                    <span className="text-xs text-muted-foreground">
                      {new Date(log.created_at).toLocaleDateString('tr-TR', { day: 'numeric', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' })}
                    </span>
                  </div>
                ))}
              </div>
            )}
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default ProductInventory;
